import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { toast } from "react-toastify";
import { auth } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import { routePaths } from "../constants/routes";
import { trackSearchLog } from "../services/analyticsService";
import { navGroups, noResultRecommendations, searchCatalog } from "../config/siteNavigation";
import "../style/navbar.css";
import logo from "../assets/qacoder-logo.svg";

const RECENT_SEARCHES_KEY = "qaCoderRecentSearches";

const defaultPopularSearches = searchCatalog.filter((item) => item.popular).slice(0, 6);

function groupByCategory(items) {
  return items.reduce((groups, item) => {
    const key = item.category;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {});
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role } = useAuth();
  const searchRef = useRef(null);
  const [isDesktopView, setIsDesktopView] = useState(() => window.innerWidth > 991);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileExploreOpen, setIsMobileExploreOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const rawValue = localStorage.getItem(RECENT_SEARCHES_KEY);
      return rawValue ? JSON.parse(rawValue) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const handleResize = () => {
      const nextDesktopValue = window.innerWidth > 991;
      setIsDesktopView(nextDesktopValue);

      if (nextDesktopValue) {
        setIsMenuOpen(false);
        setIsMobileExploreOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchPanel(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setShowSearchPanel(false);
    setActiveSuggestionIndex(-1);
    setIsMenuOpen(false);
    setIsMobileExploreOpen(false);
  }, [location.pathname]);

  const saveRecentSearch = (topic) => {
    const nextRecentSearches = [
      topic,
      ...recentSearches.filter((item) => item.to !== topic.to),
    ].slice(0, 5);

    setRecentSearches(nextRecentSearches);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(nextRecentSearches));
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsMobileExploreOpen(false);
  };

  const closeExploreMenu = () => {
    setIsMobileExploreOpen(false);
    document.activeElement?.blur?.();
  };

  const toggleMenu = () => {
    setIsMenuOpen((currentValue) => !currentValue);
  };

  const handleExploreToggle = () => {
    if (!isDesktopView) {
      setIsMobileExploreOpen((currentValue) => !currentValue);
    }
  };

  const handleExploreMouseEnter = () => {
    if (isDesktopView) {
      setIsMobileExploreOpen(true);
    }
  };

  const handleExploreMouseLeave = () => {
    if (isDesktopView) {
      setIsMobileExploreOpen(false);
    }
  };

  const handleExploreItemClick = () => {
    closeMenu();
    closeExploreMenu();
  };

  const matchedResults = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return [];
    }

    return searchCatalog
      .map((item) => {
        const haystack = [item.label, item.description, item.category, ...item.keywords]
          .join(" ")
          .toLowerCase();

        let score = 0;
        if (item.label.toLowerCase().includes(normalizedQuery)) score += 4;
        if (item.category.toLowerCase().includes(normalizedQuery)) score += 2;
        if (item.keywords.some((keyword) => keyword.toLowerCase().includes(normalizedQuery))) score += 3;
        if (haystack.includes(normalizedQuery)) score += 1;

        return { ...item, score };
      })
      .filter((item) => item.score > 0)
      .sort((firstItem, secondItem) => secondItem.score - firstItem.score)
      .slice(0, 10);
  }, [searchQuery]);

  const groupedMatchedResults = useMemo(() => groupByCategory(matchedResults), [matchedResults]);

  const popularSearches = useMemo(() => {
    return defaultPopularSearches.filter(
      (popularItem) => !recentSearches.some((recentItem) => recentItem.to === popularItem.to)
    );
  }, [recentSearches]);

  const flatSuggestions = useMemo(() => {
    if (searchQuery.trim()) {
      return matchedResults;
    }

    return [...recentSearches, ...popularSearches].slice(0, 8);
  }, [matchedResults, popularSearches, recentSearches, searchQuery]);

  const handleSearchSelection = (topic) => {
    trackSearchLog({
      queryText: searchQuery.trim() || topic.label,
      selectedLabel: topic.label,
      selectedPath: topic.to,
      category: topic.category || "Recommended",
      resultCount: flatSuggestions.length,
    });
    saveRecentSearch(topic);
    setSearchQuery("");
    setShowSearchPanel(false);
    setActiveSuggestionIndex(-1);
    closeMenu();
    navigate(topic.to);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    if (!flatSuggestions.length) {
      toast.info("No exact match found. Try a different keyword or use the recommended links.");
      return;
    }

    const selectedSuggestion =
      activeSuggestionIndex >= 0 ? flatSuggestions[activeSuggestionIndex] : flatSuggestions[0];

    handleSearchSelection(selectedSuggestion);
  };

  const handleSearchKeyDown = (event) => {
    if (!showSearchPanel) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndex((currentValue) =>
        currentValue >= flatSuggestions.length - 1 ? 0 : currentValue + 1
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndex((currentValue) =>
        currentValue <= 0 ? flatSuggestions.length - 1 : currentValue - 1
      );
    }

    if (event.key === "Escape") {
      setShowSearchPanel(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      await signOut(auth);
      localStorage.removeItem("role");
      toast.success("Logged out successfully");
      setShowConfirmLogout(false);
      closeMenu();
      navigate(routePaths.login, { replace: true });
    } catch (error) {
      toast.error(error.message || "Unable to logout right now.");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const dashboardLink =
    role === "admin" ? routePaths.admin : role === "trainer" ? routePaths.trainer : routePaths.student;
  const dashboardLabel =
    role === "admin" ? "Admin Dashboard" : role === "trainer" ? "Trainer Dashboard" : "Student Dashboard";
  const hasCrmAccess = ["admin", "trainer"].includes(role);
  const shouldShowSearchPanel = showSearchPanel && (searchQuery.trim() || recentSearches.length || popularSearches.length);

  return (
    <>
      <nav className="navbar navbar-expand-lg custom-navbar sticky-top">
        <div className="container-fluid custom-navbar-inner">
          <NavLink to={routePaths.home} className="nav-brand-link" onClick={closeMenu}>
            <img src={logo} alt="QA Coder logo" className="nav-logo-image" />
          </NavLink>

          <div className="navbar-primary-rail d-none d-xl-flex">
            <NavLink className="rail-link rail-link-free" to={routePaths.studyNotes}>
              Free Learning
            </NavLink>
            <NavLink className="rail-link rail-link-practice" to={routePaths.automationTesting}>
              Practice Area
            </NavLink>
            <NavLink className="rail-link rail-link-commerce" to={routePaths.product}>
              Product Catalog
            </NavLink>
            <NavLink className="rail-link rail-link-paid" to={routePaths.enroll}>
              Premium Course
            </NavLink>
          </div>

          <button
            className={`navbar-toggler custom-navbar-toggler ${isMenuOpen ? "is-open" : ""}`}
            type="button"
            onClick={toggleMenu}
            aria-controls="navbarSupportedContent"
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <span className="toggler-bar"></span>
            <span className="toggler-bar"></span>
            <span className="toggler-bar"></span>
          </button>

          <div
            className={`navbar-collapse custom-navbar-collapse ${isMenuOpen ? "show" : ""}`}
            id="navbarSupportedContent"
          >
            <div className="navbar-search-shell" ref={searchRef}>
              <form className="navbar-search-form" onSubmit={handleSearchSubmit}>
                <i className="fa-solid fa-magnifying-glass search-icon"></i>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setShowSearchPanel(true);
                    setActiveSuggestionIndex(-1);
                  }}
                  onFocus={() => setShowSearchPanel(true)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search topics, tools, interview prep, or courses..."
                  aria-label="Search learning topics"
                  aria-autocomplete="list"
                  aria-controls="navbar-search-results"
                  aria-activedescendant={
                    activeSuggestionIndex >= 0 && flatSuggestions[activeSuggestionIndex]
                      ? `search-suggestion-${flatSuggestions[activeSuggestionIndex].to.replaceAll("/", "")}`
                      : undefined
                  }
                />

                <button type="submit" className="search-submit-btn">
                  Search
                </button>
              </form>

              {shouldShowSearchPanel && (
                <div className="search-results-panel" id="navbar-search-results">
                  {!searchQuery.trim() ? (
                    <>
                      {recentSearches.length > 0 && (
                        <div className="search-section">
                          <div className="search-section-head">
                            <span>Recent Searches</span>
                          </div>
                          <div className="search-chip-list">
                            {recentSearches.map((topic) => (
                              <button
                                key={`recent-${topic.to}`}
                                type="button"
                                className="search-chip"
                                onClick={() => handleSearchSelection(topic)}
                              >
                                <i className="fa-solid fa-clock-rotate-left"></i>
                                {topic.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="search-section">
                        <div className="search-section-head">
                          <span>Popular Searches</span>
                        </div>
                        <div className="search-chip-list">
                          {popularSearches.slice(0, 6).map((topic) => (
                            <button
                              key={`popular-${topic.to}`}
                              type="button"
                              className="search-chip search-chip-popular"
                              onClick={() => handleSearchSelection(topic)}
                            >
                              <i className="fa-solid fa-fire"></i>
                              {topic.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : matchedResults.length > 0 ? (
                    Object.entries(groupedMatchedResults).map(([category, items]) => (
                      <div className="search-section" key={category}>
                        <div className="search-section-head">
                          <span>{category}</span>
                          <small>{items.length} result{items.length > 1 ? "s" : ""}</small>
                        </div>

                        {items.map((topic) => {
                          const itemIndex = flatSuggestions.findIndex((item) => item.to === topic.to);
                          const isActive = itemIndex === activeSuggestionIndex;

                          return (
                            <button
                              key={topic.to}
                              id={`search-suggestion-${topic.to.replaceAll("/", "")}`}
                              type="button"
                              className={`search-result-item ${isActive ? "is-active" : ""}`}
                              onMouseEnter={() => setActiveSuggestionIndex(itemIndex)}
                              onClick={() => handleSearchSelection(topic)}
                            >
                              <div className="search-result-copy">
                                <div className="search-result-topline">
                                  <strong>{topic.label}</strong>
                                  <span className="search-result-badge">{topic.category}</span>
                                </div>
                                <span>{topic.description}</span>
                              </div>
                              <i className="fa-solid fa-arrow-up-right-from-square"></i>
                            </button>
                          );
                        })}
                      </div>
                    ))
                  ) : (
                    <div className="search-empty-state">
                      <div className="search-empty-icon">
                        <i className="fa-solid fa-compass"></i>
                      </div>
                      <strong>No direct match for "{searchQuery.trim()}"</strong>
                      <p>Try one of these useful starting points instead.</p>
                      <div className="search-empty-links">
                        {noResultRecommendations.map((item) => (
                          <button
                            key={item.to}
                            type="button"
                            className="search-empty-link"
                            onClick={() => handleSearchSelection(item)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="nav-group-shell">
              <div
                className="nav-group-card nav-explore-card"
                onMouseEnter={handleExploreMouseEnter}
                onMouseLeave={handleExploreMouseLeave}
              >
                <button
                  type="button"
                  className={`nav-group-trigger nav-explore-trigger ${isMobileExploreOpen ? "is-open" : ""}`}
                  onClick={handleExploreToggle}
                  aria-expanded={isMobileExploreOpen}
                >
                  <span>Explore</span>
                  <i className="fa-solid fa-chevron-down"></i>
                </button>

                <div className={`nav-group-dropdown nav-explore-dropdown ${isMobileExploreOpen ? "is-open" : ""}`}>
                  <div className="nav-group-dropdown-head nav-explore-head">
                    <strong>Explore QA Coder</strong>
                    <span>Browse learning, practice, interview prep, blogs, and dashboards.</span>
                  </div>

                  <div className="nav-group-grid">
                    {navGroups.map((group) => (
                      <div className="nav-group-section" key={group.id}>
                        <div className="nav-group-section-head">
                          <strong>{group.label}</strong>
                          <span>{group.summary}</span>
                        </div>

                        <div className="nav-group-links">
                          {group.items.map((item) => (
                            <NavLink
                              key={item.to}
                              className="nav-group-link"
                              to={item.to}
                              onClick={handleExploreItemClick}
                            >
                              <strong>{item.label}</strong>
                              <span>{item.hint}</span>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="navbar-mobile-rails">
              <NavLink className="rail-link rail-link-free" to={routePaths.studyNotes} onClick={closeMenu}>
                Free Learning
              </NavLink>
              <NavLink className="rail-link rail-link-practice" to={routePaths.automationTesting} onClick={closeMenu}>
                Practice Area
              </NavLink>
              <NavLink className="rail-link rail-link-commerce" to={routePaths.product} onClick={closeMenu}>
                Product Catalog
              </NavLink>
              <NavLink className="rail-link rail-link-paid" to={routePaths.enroll} onClick={closeMenu}>
                Premium Course
              </NavLink>
            </div>

            <div className="navbar-action-group">
              {user ? (
                <>
                  <div className="navbar-user-chip">
                    <span className="user-chip-avatar">
                      {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
                    </span>
                    <div className="user-chip-text">
                      <strong>{user.displayName || "QA Learner"}</strong>
                      <span>{user.email}</span>
                    </div>
                  </div>

                  <NavLink className="dashboard-action-link" to={dashboardLink} onClick={closeMenu}>
                    {dashboardLabel}
                  </NavLink>

                  {hasCrmAccess && (
                    <NavLink className="dashboard-action-link crm-navbar-link" to={routePaths.leadCrm} onClick={closeMenu}>
                      Lead CRM
                    </NavLink>
                  )}

                  <NavLink className="dashboard-action-link bug-navbar-link" to={routePaths.bugs} onClick={closeMenu}>
                    Bug Tracker
                  </NavLink>

                  <button
                    type="button"
                    className="logout-action-btn"
                    onClick={() => setShowConfirmLogout(true)}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="guest-action-stack">
                  <NavLink className="storefront-action-link" to={routePaths.product} onClick={closeMenu}>
                    Browse Catalog
                  </NavLink>
                  <NavLink className="signin-action-link" to={routePaths.login} onClick={closeMenu}>
                    Sign In / Sign Up
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {showConfirmLogout && (
        <div className="logout-confirm-overlay" role="dialog" aria-modal="true">
          <div className="logout-confirm-card">
            <div className="logout-confirm-icon">
              <i className="fa-solid fa-right-from-bracket"></i>
            </div>
            <h3>Logout from your account?</h3>
            <p>
              You are currently on <strong>{location.pathname}</strong>. After logout, you
              will be redirected to the login page.
            </p>

            <div className="logout-confirm-actions">
              <button
                type="button"
                className="confirm-secondary-btn"
                onClick={() => setShowConfirmLogout(false)}
                disabled={isLoggingOut}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-primary-btn"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging out..." : "Yes, Logout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
